import { useLogger } from '../useLogger/useLogger'

import { integrateWithCalendar } from '@percent/workplace-giving/api/calendar/integrateWithCalendar'
import { okResponse } from '@percent/workplace-giving/api/goodstack'
import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { CalendarProvider } from './calendarProvider.types'
import { useGoogleCalendarAuth } from './useGoogleCalendarAuth'
import { useOutlookCalendarAuth } from './useOutlookCalendarAuth'

export { CalendarProvider }

export enum ConnectionStatus {
  CONNECTED = 'connected',
  NOT_CONNECTED = 'not_connected',
  ERROR = 'error',
  MISSING_PERMISSIONS = 'missing_permissions'
}

type UseIntegrateWithCalendarProps = {
  provider: CalendarProvider
  setLoadingState: (isLoading: boolean) => void
  setConnectionStatus: (status: ConnectionStatus) => void
  successCallback?: VoidFunction
}

export const useIntegrateWithCalendar = ({
  provider,
  setLoadingState,
  setConnectionStatus,
  successCallback
}: UseIntegrateWithCalendarProps) => {
  const { logError } = useLogger()

  const { mutateAsync } = useMutation({
    mutationFn: integrateWithCalendar,
    onSuccess: res => {
      setLoadingState(false)

      if (okResponse(res)) {
        successCallback?.()

        return setConnectionStatus(ConnectionStatus.CONNECTED)
      }

      if (res.status === 403) {
        return setConnectionStatus(ConnectionStatus.MISSING_PERMISSIONS)
      }

      return setConnectionStatus(ConnectionStatus.ERROR)
    },
    onError: error => {
      setConnectionStatus(ConnectionStatus.ERROR)
      logError(error)
      setLoadingState(false)
    }
  })

  const handleAuthSuccess = (code: string) => {
    mutateAsync({ provider, code })
  }

  const handleAuthError = (error: Error) => {
    logError(error)
    setLoadingState(false)
  }

  const googleConnectCalendar = useGoogleCalendarAuth({
    onSuccess: handleAuthSuccess,
    onError: handleAuthError
  })

  const outlookConnectCalendar = useOutlookCalendarAuth({
    onSuccess: handleAuthSuccess,
    onError: handleAuthError,
    enabled: provider === CalendarProvider.OUTLOOK
  })

  return provider === CalendarProvider.GOOGLE ? googleConnectCalendar : outlookConnectCalendar
}
